import React, {useMemo} from 'react'

interface CustomStyleProps {
    /** Raw CSS string authored in a Page Designer `custom_css` field. */
    css?: string | null
}

/**
 * Normalize whitespace that breaks CSS parsing. Authors paste custom_css into Page Designer from
 * rich editors (Word, mail, other CMSs) that silently convert indentation into NBSP (U+00A0) and
 * other Unicode spaces. A property/declaration that starts with a non-ASCII space (e.g.
 * `\u00A0\u00A0font-family: ...`) has a malformed property name, so the browser DROPS that whole
 * declaration — while ASCII-indented ones (font-size, line-height) survive. That is exactly why a
 * pasted `@font-face { <NBSP>font-family; <NBSP>src }` ends up empty and the font never loads.
 *
 * We replace NBSP and the other common invisible offenders with a regular space, and strip BOM /
 * zero-width characters, so the CSS parses as authored.
 */
const normalizeCssWhitespace = (css: string): string =>
    css
        // NBSP and Unicode space separators → regular space.
        .replace(/\u00A0|\u1680|[\u2000-\u200A]|\u202F|\u205F|\u3000/gu, ' ')
        // BOM / zero-width / word-joiner → remove.
        .replace(/\uFEFF|\u200B|\u200C|\u200D|\u2060/gu, '')

/**
 * CustomStyle injects the raw CSS authored in a Page Designer `custom_css` field as a real
 * stylesheet (`<style>` tag), so it behaves like site CSS.
 *
 * Business context: authors write custom CSS directly in Page Designer to tweak a module's
 * rendering without a dedicated customization attribute per case. That CSS uses ABSOLUTE
 * selectors (e.g. `.back.cc-pd-wall-mixed-image .cc-content-text--with-content {…}`) and
 * at-rules like `@font-face`.
 *
 * Why a `<style>` tag and not Chakra's `css` prop: Chakra/emotion's `css` prop scopes styles
 * to the element it is placed on (nested selectors are resolved relative to that element via
 * `&`). It therefore cannot apply the author's absolute selectors or register `@font-face`.
 * Rendering the string inside a `<style>` element — the same approach the editorial Widget
 * already uses — makes the CSS global, so absolute selectors match and fonts load. This mirrors
 * how the custom_css behaves in the reference storefront.
 *
 * SECURITY: `custom_css` is trusted editorial content from Page Designer, not end-user input.
 * A CSS string cannot execute script; it is injected verbatim as stylesheet text.
 */
const CustomStyle: React.FC<CustomStyleProps> = ({css}) => {
    const normalized = useMemo(() => (css ? normalizeCssWhitespace(css).trim() : ''), [css])

    if (!normalized) return null

    // Inject via dangerouslySetInnerHTML (not as JSX children): it is the reliable way to render a
    // raw CSS string into a <style> element. Passing CSS as text children can trip React's child
    // handling/escaping and hydration, leaving the stylesheet present in the DOM but inert.
    return <style dangerouslySetInnerHTML={{__html: normalized}} />
}

export {CustomStyle}
export type {CustomStyleProps}
